import { PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";

import useApiRequest from "@/lib/hooks/use-request";
import {
  PipelineDetailsResponseDto,
  StageDetailsResponseDto,
  WPPipelineSelectableEntityResponseDto,
} from "@/lib/interfaces/pipelines";
import { UserSelectableEntityResponseDto } from "@/lib/interfaces/users";
import PageNotFound from "@/page-components/error/page-not-found";
import UserLinked from "@/page-components/settings/users/users-linked";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import TitleForm from "@/ui-components/ui/title-form";
import { PageTitle } from "@/ui-components/utils/menu-items/page-title";
import { PlusIcon, UpdateIcon } from "@radix-ui/react-icons";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { useParams } from "react-router";

const PipelineDetails = () => {
  const { id } = useParams();
  return <>{id ? <PipelineDetailsFetcher id={id} /> : <PageNotFound />}</>;
};

const PipelineDetailsFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<PipelineDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<PipelineDetailsResponseDto>(`pipelines/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: PipelineDetailsResponseDto) => (
        <PipelineContent setReload={setReload} item={loadedItem} />
      )}
    />
  );
};

const PipelineContent = ({
  item,
  setReload,
}: {
  item: PipelineDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle } = useBreadcrumb();
  const [stages, setStages] = useState<StageDetailsResponseDto[]>(item.stages);
  const { apiRequest } = useApiRequest();
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    if (setTitle) {
      setTitle(item.title);
    }
  }, [item, setTitle]);

  const updateTitle = async (title: string) => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${item._id}`, "PATCH", {
      body: {
        title: title,
      },
    });
    setIsEditing(false);
    setReload((prev) => !prev);
  };

  const createStage = async (title: string) => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${item._id}/stages`, "POST", {
      body: {
        title: title,
      },
    });
    setIsEditing(false);
    setReload((prev) => !prev);
  };

  return (
    <div>
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center">
          <PageTitle>
            <div className="flex flex-row gap-2 items-center">
              Pipeline:{" "}
              {isEditing ? (
                <TitleForm
                  onSubmit={(values) => updateTitle(values.title)}
                  defaultValue={item.title}
                  onCancel={() => setIsEditing(false)}
                />
              ) : (
                <span onClick={() => setIsEditing(true)}>{item.title}</span>
              )}
            </div>
          </PageTitle>
          <PipelineSelection
            pipelines={item.pipelines}
            selectedPipeline={item.pipelines.find((pipeline) => pipeline.id === item.wordpressId)}
            id={item._id}
            setReload={setReload}
          />
        </div>
      </PageTitleContainer>
      <div className="p-4">
        <UsersManager users={item.users} item={item} setReload={setReload} />
        <StageManager stages={stages} setStages={setStages} id={item._id} createStage={createStage} />
      </div>
    </div>
  );
};

const PipelineSelection = ({
  pipelines,
  selectedPipeline,
  id,
  setReload,
}: {
  pipelines: WPPipelineSelectableEntityResponseDto[];
  selectedPipeline?: WPPipelineSelectableEntityResponseDto;
  id: string;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState(selectedPipeline?.id);
  const [somethingChanged, setSomethingChanged] = useState(false);
  const { apiRequest } = useApiRequest();

  const updatePipeline = async (pipelineId: string) => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${id}`, "PATCH", {
      body: {
        wordpressId: pipelineId === "undefined" ? undefined : pipelineId,
      },
    });
    setIsEditing(false);
    setReload((prev) => !prev);
    setSomethingChanged(false);
  };

  return (
    <div>
      {isEditing ? (
        <div className="flex flex-row gap-2 items-center">
          <Select
            onValueChange={(value) => {
              setSelectedPipelineId(value);
              setSomethingChanged(true);
            }}
            defaultValue={selectedPipelineId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Pipeline auswählen" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.value}
                </SelectItem>
              ))}
              <SelectItem value="undefined">Keine Pipeline</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => updatePipeline(selectedPipelineId || "")} disabled={!somethingChanged}>
            Speichern
          </Button>
        </div>
      ) : (
        <Button onClick={() => setIsEditing(true)} variant="ghost">{selectedPipeline?.value || "Pipeline auswählen"}</Button>
      )}
    </div>
  );
};

const UsersManager = ({
  users,
  item,
  setReload,
}: {
  users: UserSelectableEntityResponseDto[];
  item: PipelineDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();

  const addUser = async (userId: string) => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${item._id}/users`, "POST", {
      body: {
        userId: userId,
      },
    });
    setReload((prev) => !prev);
  };

  const removeUser = async (userId: string) => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${item._id}/users/${userId}`, "DELETE");
    setReload((prev) => !prev);
  };

  return (
    <div className="flex flex-col gap-2 mb-2 grow">
      <div className="flex flex-col gap-2 max-w-[50%]">
        <h3 className="font-medium text-gray-900">Benutzer</h3>
        <div>
          <UserLinked
            multiple
            item={users}
            onAssign={async (userId: string, title: string) => {
              await addUser(userId);
            }}
            onRemove={async (id: string) => {
              await removeUser(id);
            }}
          />
        </div>
      </div>
      <hr />
    </div>
  );
};

const StageManager = ({
  stages,
  setStages,
  createStage,
  id,
}: {
  stages: StageDetailsResponseDto[];
  setStages: Dispatch<SetStateAction<StageDetailsResponseDto[]>>;
  createStage: (title: string) => Promise<void>;
  id: string;
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [somethingChanged, setSomethingChanged] = useState(false);
  const { apiRequest } = useApiRequest();

  const handleUpdate = async () => {
    await apiRequest<PipelineDetailsResponseDto>(`pipelines/${id}/stages`, "PATCH", {
      body: {
        stages: stages,
      },
      toast: {
        toastText: "Pipeline erfolgreich aktualisiert",
      },
    });
    setSomethingChanged(false);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(stages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    items.forEach((stage, index) => {
      stage.sort = index;
    });

    setStages(items);
    setSomethingChanged(true);
  };

  return (
    <div className="flex flex-col gap-2">
      {isCreating ? (
        <div className="flex flex-row gap-2 items-center pb-4">
          <TitleForm
            onSubmit={(values) => createStage(values.title)}
            defaultValue={""}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      ) : (
        <div className="flex flex-row gap-2 items-center justify-between">
          <div
            className={`flex-shrink-0 w-48 h-14 bg-white mb-4 rounded-lg shadow-sm border mx-2 p-4 flex flex-row gap-2 items-center cursor-pointer`}
            onClick={() => setIsCreating(true)}
          >
            <PlusIcon className="h-4 w-4" />
            <h3 className="font-medium text-gray-900">Stage hinzufügen</h3>
          </div>
          <Button className="gap-2" disabled={!somethingChanged} onClick={handleUpdate}>
            <UpdateIcon className="h-4 w-4" />
            Aktualisieren
          </Button>
        </div>
      )}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="stages" direction="horizontal">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-row overflow-x-auto pb-4">
              {stages.map((stage, index) => (
                <Draggable key={stage._id} draggableId={stage._id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`flex-shrink-0 w-48 bg-white rounded-lg shadow-sm border mx-2 ${
                        snapshot.isDragging ? "shadow-lg" : ""
                      }`}
                    >
                      <Stage stage={stage} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

const Stage = ({ stage }: { stage: StageDetailsResponseDto }) => {
  return (
    <div className="p-4">
      <h3 className="font-medium text-gray-900">{stage.title}</h3>
      {/* Add more stage content here */}
    </div>
  );
};

export default PipelineDetails;
